import { useState, type FormEvent, type ReactNode } from 'react';
import { passwordSchema } from '@cercle/shared';
import { useApp } from '@/shared/app-context';
import { errorMessage } from '@/shared/http/http-client';
import { Button, Card, ErrorText, Field, Muted } from '@/shared/ui';
import { PasswordInput } from '../auth/PasswordInput';
import styles from './profile.module.scss';

/**
 * Changement de mot de passe.
 *
 * Le changement révoque toutes les sessions, y compris celle-ci : c'est le comportement
 * attendu de ce geste — on change son mot de passe pour chasser quelqu'un, pas pour le
 * laisser connecté ailleurs. L'écran prévient et redirige vers la connexion.
 */
export function ChangePasswordCard(): ReactNode {
  const { api, setMember } = useApp();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    const parsed = passwordSchema.safeParse(next);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Mot de passe invalide.');
      return;
    }
    if (next === current) {
      setError('Le nouveau mot de passe doit être différent de l’ancien.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await api.auth.changePassword({ currentPassword: current, newPassword: parsed.data });
      // Le serveur a révoqué la session : on remet le front dans l'état déconnecté plutôt
      // que d'attendre le premier 401 sur un écran quelconque.
      setMember(null);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className={styles.form}>
      <form className={styles.form} onSubmit={(event) => void submit(event)} noValidate>
        <Field label="Mot de passe actuel">
          <PasswordInput autoComplete="current-password" value={current} onChange={setCurrent} />
        </Field>

        <Field label="Nouveau mot de passe">
          <PasswordInput
            autoComplete="new-password"
            value={next}
            onChange={setNext}
            showStrengthHint
            required
          />
        </Field>

        <ErrorText>{error}</ErrorText>
        <Button type="submit" disabled={busy || next.length === 0}>
          {busy ? 'Enregistrement…' : 'Changer mon mot de passe'}
        </Button>
        <Muted>
          Vos autres appareils seront déconnectés, et vous devrez vous reconnecter ici.
        </Muted>
      </form>
    </Card>
  );
}
